import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { checkIfFiltersValuesIsEmpty } from '../../../../../utils';
import { useOffers } from '../../hooks/useOffer';
import { useCampanhaStore } from '../../store/campanha-store';

const sortColumnsTypes = {
  name: 'name',
};

export type SortType = keyof typeof sortColumnsTypes;

export const useOfferTable = () => {
  const { id } = useParams<{ id: string }>();

  const { selectedIds, selectedAll, setSelectedAll, setSelectedIds, ofertasFilters } = useCampanhaStore(id);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort, setSort] = useState<{
    type: SortType;
    orderBy: 'asc' | 'desc';
  }>({
    type: 'name',
    orderBy: 'asc',
  });

  const apiFilters = useMemo(() => {
    const result: Record<string, string[]> = {};

    const mapKeyToApi = (key: string): string | null => {
      if (key === 'productIds') return 'product_ids';
      if (key === 'productFamilyIds') return 'product_family_ids';
      if (key === 'productBrand') return 'product_brands';
      if (key === 'supplier') return 'suppliers';
      if (key === 'offerType') return 'offer_types';
      if (key === 'offerBlock') return 'offer_group_names';

      const categoryMatch = key.match(/^categoryLevel(\d+)$/);
      if (categoryMatch?.[1]) return `category_level${categoryMatch[1]}`;

      return null;
    };

    Object.entries(ofertasFilters ?? {}).forEach(([key, value]) => {
      const apiKey = mapKeyToApi(key);
      if (!apiKey) return;
      if (!Array.isArray(value)) return;
      result[apiKey] = value;
    });

    return result;
  }, [ofertasFilters]);

  const params = useMemo(
    () => ({
      page: page || null,
      size: size || null,
      sort: sort?.type && sort?.orderBy ? `${sortColumnsTypes[sort.type]},${sort.orderBy}` : null,
      filters: apiFilters,
    }),
    [page, size, sort, apiFilters],
  );

  const {
    states: { offers },
    handlers: { refetchOffers: refetch },
  } = useOffers(id, params);

  const isCheckedAll = useMemo(() => {
    const totalElements = offers?.totalElements ?? 0;

    return (totalElements > 0 && totalElements === selectedIds.length) || selectedAll;
  }, [offers?.totalElements, selectedIds, selectedAll]);

  const isIndeterminate = useMemo(() => !isCheckedAll && !!selectedIds.length, [isCheckedAll, selectedIds]);
  const hasActiveFilters = useMemo(() => !checkIfFiltersValuesIsEmpty(ofertasFilters ?? {}), [ofertasFilters]);

  const handleCheckAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds([]);
    }
    setSelectedAll(checked);
  };

  const handleCheckItem = (checked: boolean, itemId: string) => {
    if (checked) {
      setSelectedIds([...selectedIds, itemId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== itemId));
    }
  };

  const isChecked = useCallback(
    (itemId: string) => {
      if (selectedAll) {
        return true;
      }
      return !!selectedIds.includes(itemId);
    },
    [selectedIds, selectedAll],
  );

  return {
    states: {
      sort,
      offers,
      isCheckedAll,
      isIndeterminate,
      hasActiveFilters,
    },
    handlers: {
      handleCheckAll,
      handleCheckItem,
      isChecked,
      setSort,
      setPage,
      setSize,
      refetch,
    },
  };
};
